/**
 * Expanded detail for a per-booking opportunity root (`kind:
 * "booking_opportunity"`). Renders the single booking's pricing comparison
 * (carrier -> contract, lane + dates, parties) using the same hydrated row the
 * commercial digest used, so a rebook / first-book row is fully actionable and
 * reviewable on its own instead of being buried in a grouped digest.
 */
import { useUserPreferences } from "@/context/UserPreferencesContext";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { usePortNames } from "@/hooks/usePortNames";
import { extractLaneLocodes } from "@/lib/formatPort";
import { cn } from "@/lib/utils";
import type { BookingOpportunityPayload } from "@/types/feed";
import {
  DigestBookingRow,
  useHydratedDigestBookings,
} from "./FeedCommercialDigestDetail";

/** Whether a payload is a per-booking opportunity root. */
export function isBookingOpportunityPayload(
  payload: unknown,
): payload is BookingOpportunityPayload {
  if (!payload || typeof payload !== "object") return false;
  const p = payload as Record<string, unknown>;
  return (
    p.kind === "booking_opportunity" &&
    !!p.booking &&
    typeof p.booking === "object"
  );
}

export function FeedBookingOpportunityDetail({
  payload,
}: {
  payload: BookingOpportunityPayload;
}) {
  const { preferences } = useUserPreferences();
  const { formatDate } = useDateFormatter();
  const bookings = useHydratedDigestBookings([payload.booking]);
  const laneLocodes = extractLaneLocodes(...bookings.map((b) => b.lane_key));
  const portNames = usePortNames(laneLocodes);

  const isRebook = payload.dimension === "rebook_opportunity";
  const booking = bookings[0];
  if (!booking) return null;

  return (
    <div
      className={cn(
        "feed-digest",
        isRebook ? "feed-digest--rebook" : "feed-digest--first-book",
      )}
    >
      <div className="feed-digest__list">
        <DigestBookingRow
          booking={booking}
          isRebook={isRebook}
          showLocodes={preferences.showLocodes}
          portNames={portNames}
          formatDate={formatDate}
        />
      </div>
    </div>
  );
}
